import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authApi, type ApiError } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/Button';
import Card from '../components/Card';
import Input from '../components/Input';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    publicNameMode: 'first_plus_initial' as const
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validation functions matching backend rules
  const validateName = (name: string, field: 'first' | 'last'): string | null => {
    if (!name.trim()) {
      return `${field === 'first' ? 'First' : 'Last'} name is required`;
    }
    
    const maxLength = field === 'first' ? 50 : 80;
    if (name.length > maxLength) {
      return `${field === 'first' ? 'First' : 'Last'} name should be ${maxLength} characters or less`;
    }
    
    const nameRegex = /^[a-zA-Z\s\-']+$/;
    if (!nameRegex.test(name.trim())) {
      return `${field === 'first' ? 'First' : 'Last'} name can only contain letters, spaces, hyphens, and apostrophes`;
    }
    
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    // Client-side validation
    const firstNameError = validateName(formData.firstName, 'first');
    const lastNameError = validateName(formData.lastName, 'last');
    
    if (firstNameError) {
      setError(firstNameError);
      return;
    }
    
    if (lastNameError) {
      setError(lastNameError);
      return;
    }
    
    setLoading(true);
    setError(null);

    try {
      // Call registration endpoint
      await authApi.register({
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        public_name_mode: formData.publicNameMode
      });
      
      // Invalidate the ['me'] query to refetch updated user
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      
      // Redirect to clubs page
      navigate('/clubs');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // If user is already complete, redirect
  if (user && user.first_name && user.last_name) {
    navigate('/clubs');
    return null;
  }

  return (
    <div className="max-w-md mx-auto space-y-6">
      <div className="text-center space-y-2">
        <p className="text-sm uppercase tracking-[0.3em] text-text-muted">Welcome</p>
        <h1 className="text-h1 font-semibold">Your name</h1>
        <p className="text-text-secondary">
          We use your real name so club leaders can manage rosters and attendance. 
          You'll be able to choose how your name appears publicly.
        </p>
      </div>

      <Card className="p-6">
        {error && (
          <div className="mb-6 rounded-md border border-red-500/40 bg-red-500/10 p-4">
            <p className="text-sm text-red-200 text-center">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* First Name */}
          <div className="space-y-2">
            <label
              htmlFor="firstName"
              className="block text-sm font-medium tracking-wide text-text-secondary"
            >
              First Name <span className="text-red-400">*</span>
            </label>
            <Input
              type="text"
              id="firstName"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              disabled={loading}
              placeholder="First name"
              required
              maxLength={50}
              size="lg"
            />
          </div>

          {/* Last Name */}
          <div className="space-y-2">
            <label
              htmlFor="lastName"
              className="block text-sm font-medium tracking-wide text-text-secondary"
            >
              Last Name <span className="text-red-400">*</span>
            </label>
            <Input
              type="text"
              id="lastName"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              disabled={loading}
              placeholder="Last name"
              required
              maxLength={80}
              size="lg"
            />
          </div>

          <Button 
            type="submit" 
            disabled={loading || !formData.firstName.trim() || !formData.lastName.trim()}
            loading={loading}
            className="w-full"
            size="lg"
          >
            Complete Setup
          </Button>
        </form>
      </Card>
    </div>
  );
}